import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import { Badge } from '@/components/ui-kit/badge';
import { Progress } from '@/components/ui-kit/progress';
import { ResourceBreakdown } from '../../types/timesheet-manager.types';

export const formatHours = (hours: number) => `${hours.toFixed(2)}h`;

type HourField = 'Billable' | 'GeneralAdmin' | 'SalesAndMarketing';

const totalByField = (resource: ResourceBreakdown, field: HourField) =>
  resource.Customers.reduce(
    (sum, customer) =>
      sum + customer.Projects.reduce((projectSum, project) => projectSum + project[field], 0),
    0
  );

const customerNames = (resource: ResourceBreakdown) =>
  resource.Customers.map((customer) => customer.CustomerName);
const projectNames = (resource: ResourceBreakdown) =>
  resource.Customers.flatMap((customer) => customer.Projects.map((project) => project.ProjectName));

/** Billable hours as a whole % of all hours (0 when nothing is logged). */
const billableShare = (billable: number, total: number) =>
  total > 0 ? Math.round((billable / total) * 100) : 0;

const MAX_CHIPS = 2;

/** Names as outlined chips, the first two and "+N" for the rest (all of them in the tooltip). */
const NameChips = ({ names }: { names: string[] }) => {
  if (!names.length) return <span className="text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap gap-1" title={names.join(', ')}>
      {names.slice(0, MAX_CHIPS).map((name) => (
        <Badge key={name} variant="outline" className="max-w-40 font-normal">
          <span className="truncate">{name}</span>
        </Badge>
      ))}
      {names.length > MAX_CHIPS && (
        <Badge variant="outline" className="font-normal text-muted-foreground">
          +{names.length - MAX_CHIPS}
        </Badge>
      )}
    </span>
  );
};

const ShareBar = ({ share }: { share: number }) => (
  <span className="flex items-center justify-end gap-2">
    <Progress value={share} className="h-1.5 w-16" />
    <span className="w-10 text-right tabular-nums">{share}%</span>
  </span>
);

/** The rollup's totals row, keyed by column id. */
export const rollupTotals = (resources: readonly ResourceBreakdown[]) => {
  const sum = (field: HourField) =>
    resources.reduce((total, resource) => total + totalByField(resource, field), 0);
  const billable = sum('Billable');
  const grandTotal = resources.reduce((total, resource) => total + resource.GrandTotal, 0);
  return {
    Billable: formatHours(billable),
    GeneralAdmin: formatHours(sum('GeneralAdmin')),
    SalesAndMarketing: formatHours(sum('SalesAndMarketing')),
    GrandTotal: formatHours(grandTotal),
    BillableShare: <ShareBar share={billableShare(billable, grandTotal)} />,
  };
};

interface CreateTimesheetRollupTableColumnsProps {
  t: (key: string) => string;
}

/**
 * Team Timesheets rollup (HR Module Prototype, Component Map › Team Timesheets rollup): one row per
 * person with their customers and projects as chips, the hours by kind, the grand total and the
 * billable share. Aggregated in the browser from every matching entry, so the table sorts and filters
 * the whole list itself (`matchesColumnFilter`); column ids need not be schema fields here.
 */
export const createTimesheetRollupTableColumns = ({
  t,
}: CreateTimesheetRollupTableColumnsProps): ColumnDef<ResourceBreakdown>[] => [
  {
    id: 'ResourceName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_RESOURCE')} />
    ),
    accessorFn: (row) => row.ResourceName,
    cell: ({ row }) => (
      <UserNameCell name={row.original.ResourceName} email={row.original.ResourceEmail} />
    ),
  },
  {
    id: 'DepartmentName',
    // Card: under the employee's name. Filtered by the toolbar's department dropdown, not here.
    meta: { mobile: 'subtitle' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DEPARTMENT')} />,
    accessorFn: (row) => row.DepartmentName ?? '-',
  },
  {
    id: 'Customers',
    enableSorting: false,
    meta: { filter: { type: 'text' }, numeric: false },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_CUSTOMERS')} />
    ),
    accessorFn: (row) => customerNames(row).join(', '),
    cell: ({ row }) => <NameChips names={customerNames(row.original)} />,
  },
  {
    id: 'Projects',
    enableSorting: false,
    meta: { filter: { type: 'text' }, numeric: false },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('PROJECTS')} />,
    accessorFn: (row) => projectNames(row).join(', '),
    cell: ({ row }) => <NameChips names={projectNames(row.original)} />,
  },
  {
    id: 'Billable',
    meta: { filter: { type: 'number' }, numeric: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_BILLABLE')} />
    ),
    accessorFn: (row) => totalByField(row, 'Billable'),
    cell: ({ row }) => formatHours(totalByField(row.original, 'Billable')),
  },
  {
    id: 'GeneralAdmin',
    meta: { filter: { type: 'number' }, numeric: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_GENERAL_ADMIN')} />
    ),
    accessorFn: (row) => totalByField(row, 'GeneralAdmin'),
    cell: ({ row }) => formatHours(totalByField(row.original, 'GeneralAdmin')),
  },
  {
    id: 'SalesAndMarketing',
    meta: { filter: { type: 'number' }, numeric: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_SALES_MARKETING')} />
    ),
    accessorFn: (row) => totalByField(row, 'SalesAndMarketing'),
    cell: ({ row }) => formatHours(totalByField(row.original, 'SalesAndMarketing')),
  },
  {
    id: 'GrandTotal',
    // Card: top right, as the old portal's grand total.
    meta: { filter: { type: 'number' }, mobile: 'badge', numeric: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_GRAND_TOTAL')} />
    ),
    accessorFn: (row) => row.GrandTotal,
    cell: ({ row }) => <span className="font-medium">{formatHours(row.original.GrandTotal)}</span>,
  },
  {
    id: 'BillableShare',
    meta: { filter: { type: 'number' }, numeric: true },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TIMESHEET_MANAGER_BILLABLE_SHARE')} />
    ),
    accessorFn: (row) => billableShare(totalByField(row, 'Billable'), row.GrandTotal),
    cell: ({ row }) => (
      <ShareBar share={billableShare(totalByField(row.original, 'Billable'), row.original.GrandTotal)} />
    ),
  },
];
